/**
 * Pure helpers for the video-note recorder: how the camera is opened (an injectable strategy, so the
 * dashboard and a future extension surface share one component), which container the browser can
 * record, and how fast the teleprompter scrolls.
 */

/** Longest recording, in ms. The server trims to the same cap when it transcodes. */
export const VIDEO_NOTE_MAX_MS = 60_000;
/** Seconds counted down on screen before recording starts, so the rep can look up from the script. */
export const VIDEO_NOTE_COUNTDOWN_SECONDS = 3;

/**
 * How the recorder gets a camera + mic stream. The dashboard uses `getUserMedia` directly; a surface
 * where that is unreliable (the Chrome extension side panel) can inject its own.
 */
export type VideoMediaStrategy = {
  getStream: () => Promise<MediaStream>;
  /** Which device the browser has already blocked (null = unknown). Optional: absent → just try the camera. */
  getBlockedDevices?: () => Promise<BlockedDevices | null>;
};

/**
 * Portrait, front camera, 720×1280 ideal — the shape the server transcodes to anyway — and never
 * more than 1080p either way: `ideal` alone is a hint, and a phone-as-webcam sent 1216×2160 (2026-10-16),
 * which made the server decode ~5× the pixels it keeps. A camera that can't meet the cap falls
 * back to the looser constraints below.
 */
export const VIDEO_NOTE_CONSTRAINTS: MediaStreamConstraints = {
  audio: true,
  video: {
    facingMode: 'user',
    width: { ideal: 720, max: 1080 },
    height: { ideal: 1280, max: 1920 },
    aspectRatio: { ideal: 9 / 16 },
  },
};

/**
 * The recording's video bitrate: plenty for a 720×1280 talking head (the server re-encodes to
 * that anyway) while keeping a 60 s take around 20 MB, so the upload and the server's decode stay quick.
 */
export const VIDEO_NOTE_RECORD_BITS_PER_SECOND = 2_500_000;

/** Looser fallbacks when a camera can't satisfy the portrait ideal — the frame is cropped to 9:16 on screen anyway. */
export const VIDEO_NOTE_FALLBACK_CONSTRAINTS: MediaStreamConstraints[] = [
  { audio: true, video: { facingMode: 'user' } },
  { audio: true, video: true },
];

/**
 * Open the camera with the portrait ideal; if this camera can't meet it (OverconstrainedError, or a
 * NotFoundError some browsers raise for an unmet constraint), retry with looser constraints before
 * giving up. Any other failure (permission, camera in use) is rethrown as-is.
 */
export async function openVideoNoteStream(
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>,
): Promise<MediaStream> {
  let lastError: unknown;
  for (const constraints of [VIDEO_NOTE_CONSTRAINTS, ...VIDEO_NOTE_FALLBACK_CONSTRAINTS]) {
    try {
      return await getUserMedia(constraints);
    } catch (error) {
      lastError = error;
      const name = error instanceof Error ? error.name : '';
      if (name !== 'OverconstrainedError' && name !== 'NotFoundError') throw error;
    }
  }
  throw lastError;
}

export const browserVideoMediaStrategy: VideoMediaStrategy = {
  getStream: () => openVideoNoteStream((c) => navigator.mediaDevices.getUserMedia(c)),
  getBlockedDevices: () =>
    queryBlockedDevices(
      typeof navigator !== 'undefined' && navigator.permissions != null
        ? (descriptor) => navigator.permissions.query(descriptor)
        : undefined,
    ),
};

/** Preference order: MP4 where the browser can record it (Safari), else WebM. The server transcodes either way. */
export const VIDEO_NOTE_MIME_PREFERENCE = [
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
] as const;

/** First container this browser can record, or undefined to let MediaRecorder pick its default. */
export function pickVideoNoteMimeType(isTypeSupported: (type: string) => boolean): string | undefined {
  return VIDEO_NOTE_MIME_PREFERENCE.find((type) => isTypeSupported(type));
}

export type TeleprompterSpeed = 'slow' | 'normal' | 'fast';

/** Spoken words per minute for each speed — a relaxed on-camera read sits around 140–150. */
export const TELEPROMPTER_WPM: Record<TeleprompterSpeed, number> = { slow: 115, normal: 145, fast: 175 };

/** How long the script takes to scroll past at a given speed (never shorter than 4 s, so it stays readable). */
export function teleprompterDurationMs(script: string, speed: TeleprompterSpeed): number {
  const words = script.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(4_000, Math.round((words / TELEPROMPTER_WPM[speed]) * 60_000));
}

/** mm:ss for the on-screen timer. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export type CameraErrorKind = 'denied' | 'no_camera' | 'in_use' | 'other';

/** Why the camera could not open, mapped from the DOMException name getUserMedia rejects with. */
export function cameraErrorKind(error: unknown): CameraErrorKind {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no_camera';
  // The device exists but the OS/another app holds it (Zoom, FaceTime, another tab).
  if (name === 'NotReadableError' || name === 'AbortError') return 'in_use';
  return 'other';
}

/** Which of the two devices the browser has blocked for this site. Both are needed: LinkedIn video needs sound. */
export type BlockedDevices = { camera: boolean; microphone: boolean };

/**
 * Ask the Permissions API which device is blocked, so the message names the right one — and so a site
 * that is already blocked shows the fix straight away instead of a prompt that will never appear.
 * Resolves null where the API (or these permission names) isn't supported, e.g. older Safari/Firefox.
 */
export async function queryBlockedDevices(
  query: ((descriptor: PermissionDescriptor) => Promise<{ state: PermissionState }>) | undefined,
): Promise<BlockedDevices | null> {
  if (query == null) return null;
  try {
    const [camera, microphone] = await Promise.all([query({ name: 'camera' }), query({ name: 'microphone' })]);
    return { camera: camera.state === 'denied', microphone: microphone.state === 'denied' };
  } catch {
    return null;
  }
}
